import { computed } from "vue";
import { useGatingStrategies } from "./use-gating-strategies";
import type {
  GatingStrategy,
  GatingStrategyType,
} from "../strategies/gating-strategy";
import type {
  AuthorizationRuleForGating,
  AuthorizationRuleGroupForGating,
} from "../use-gating-permissions";
import {
  channelPermissionsMap,
  type ChannelPermission,
} from "@f3/shared/permissions";
import {
  GatingAccessType,
  type GatingMaskInformation,
  type GatingRuleMetadata,
} from "../gating-types";
import { CRITERION_TYPE_BY_KEY } from "../criterion-types/gating-criterion-type-registry";
import type { GatingCriterionMetadata } from "../criterion-types/gating-criterion-type";

/**
 * Unified gating evaluator.
 *
 * This is the ONLY public entry point for gating logic.
 * Strategies are loaded automatically based on channel capabilities.
 *
 * @example
 * const { matchRule, getRelevantRule, isReady } = useGatingEvaluator();
 *
 * const hasAccess = computed(() => {
 *   if (!isReady.value) return false;
 *   return matchRule(rule);
 * });
 */
export function useGatingEvaluator() {
  const { strategies: enabledStrategies, isReady } = useGatingStrategies();

  function getStrategiesForRule(
    rule: AuthorizationRuleForGating,
  ): GatingStrategy[] {
    const result: GatingStrategy[] = [];

    for (const strategy of enabledStrategies.value) {
      if (strategy.canEvaluate(rule)) {
        result.push(strategy);
      }
    }

    return result;
  }

  /**
   * Finds the types of gating strategies that apply to a given rule.
   *
   * @param rule - The authorization rule to match
   * @returns a list of matching strategies
   */
  function getGatingTypesForRule(
    rule: AuthorizationRuleForGating,
  ): GatingStrategyType[] {
    return getStrategiesForRule(rule).map((s) => s.strategyType);
  }

  function getMetadataForGatingType(
    type: GatingStrategyType,
  ): GatingCriterionMetadata {
    return CRITERION_TYPE_BY_KEY.get(type)!;
  }

  /**
   * Evaluate whether the current user has access based on a rule.
   *
   * @param rule - The authorization rule to evaluate
   * @returns true if user has access, false otherwise
   */
  function matchRule(rule: AuthorizationRuleForGating): boolean {
    const strategies = getStrategiesForRule(rule);

    if (!strategies.length) return true; // No strategies means no gating for this rule

    return strategies.some((strategy) => strategy.matchRule(rule));
  }

  /**
   * Find the relevant rule for a given permission within a rule group.
   *
   * @param group - The authorization rule group to search
   * @param permission - The permission to find a rule for
   * @returns The matching rule or null
   */
  function getRelevantRule(
    group: AuthorizationRuleGroupForGating | null,
    permission: ChannelPermission,
  ): AuthorizationRuleForGating | null {
    if (!group) return null;
    const permissionMask = channelPermissionsMap[permission];

    for (const rule of group.authorization_rules) {
      if (
        (rule.significant_permissions_mask & permissionMask) ===
        permissionMask
      ) {
        return rule;
      }
    }
    return null;
  }

  /**
   * Extract gating mask information from a rule.
   */
  function getGatingMaskInformation(
    rule: AuthorizationRuleForGating | null,
  ): GatingMaskInformation | null {
    if (!rule) return null;
    const strategies = getStrategiesForRule(rule);

    if (strategies.length === 0) return null;

    const maskInfo: GatingMaskInformation = {
      permission_mask: 0,
      permission_mask_level: 0,
    };

    for (const strategy of strategies) {
      const info = strategy.getGatingMaskInformation(rule);
      if (!info) continue;

      // Combine masks by taking the max level for each permission bit
      maskInfo.permission_mask |= info.permission_mask;
      maskInfo.permission_mask_level = Math.max(
        maskInfo.permission_mask_level,
        info.permission_mask_level,
      );
    }

    return maskInfo;
  }

  /**
   * Extract gating metadata from a rule.
   */
  function getGatingRuleMetadata(
    rule: AuthorizationRuleForGating | null,
  ): GatingRuleMetadata | null {
    if (!rule) return null;
    const someLimited =
      getStrategiesForRule(rule)?.some(
        (s) =>
          s.getGatingRuleMetadata(rule)?.accessType ===
          GatingAccessType.limited,
      ) ?? null;

    return {
      accessType: someLimited
        ? GatingAccessType.limited
        : GatingAccessType.public,
    };
  }

  /**
   * Whether any gating strategies are enabled for the current channel.
   */
  const hasGatingEnabled = computed(() => enabledStrategies.value.length > 0);

  return {
    // Evaluation methods
    getGatingTypesForRule,
    matchRule,
    getRelevantRule,
    getGatingMaskInformation,
    getGatingRuleMetadata,
    getMetadataForGatingType,

    // State
    hasGatingEnabled,
    isReady,
  };
}
